import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import api from '@/api/activity.service';
import './style.sass';
import { CURRENT_STATUS_MAP, getScheduleTypeByValue, getWhereByValue } from '@/constants/dailyLogs';
import { IMAGES_SIZE, WINDOW_WIDTH } from '@/constants/ui';
import { Grid, EmptyResult, Avatar, BrillMask, FormsCollection } from '@/ui-kit';
import { getImageUrl } from '@/utils/helpers/requestHelpers';
import { useUrlParams, useWindowWidth } from '@/utils/hooks';

export const DailyLogsList = () => {
  const history = useHistory();
  const windowWidth = useWindowWidth();

  const [dailyLogs, setDailyLogs] = useState([]);
  const [usersWithNoEntries, setUsersWithNoEntries] = useState([]);

  const { getUrlParam, setUrlParam } = useUrlParams({});

  const today = moment().format('YYYY-MM-DD');
  const date = getUrlParam('date') ?? today;

  useEffect(() => {
    const fetchDailyLogs = async () => {
      try {
        const response = await api.getTodayDailyLogs({
          date,
        });
        setDailyLogs(response.workHours);
        setUsersWithNoEntries(response.usersWithNoEntries);
      } catch (error) {
        console.error('Error fetching daily logs:', error);
      }
    };

    fetchDailyLogs();
  }, [date]);

  const calculateTotalHours = (entries) => {
    if (!entries || entries.length === 0) return '0h';

    const totalMinutes = entries.reduce((sum, entry) => {
      if (entry.schedule_type !== 'WORK') {
        return sum;
      }

      const [startH, startM] = entry.start_time.split(':').map(Number);
      const [endH, endM] = entry.end_time.split(':').map(Number);

      const startTotalMin = startH * 60 + startM;
      const endTotalMin = endH * 60 + endM;

      return sum + (endTotalMin - startTotalMin);
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const getCurrentStatus = (entries) => {
    if (!entries || entries.length === 0) return 'N/A';

    const now = moment();
    const currentTime = now.hours() * 60 + now.minutes();

    const activeEntry = entries.find((entry) => {
      const [startH, startM] = entry.start_time.split(':').map(Number);
      const [endH, endM] = entry.end_time.split(':').map(Number);

      const startTotalMin = startH * 60 + startM;
      const endTotalMin = endH * 60 + endM;

      return currentTime >= startTotalMin && currentTime <= endTotalMin;
    });

    return CURRENT_STATUS_MAP.get(activeEntry?.work_place ?? 'N/A');
  };

  return (
    <>
      <div className="table-daily-logs__date-picker">
        <div className="table-daily-logs__date-picker-label">Date</div>
        <FormsCollection.DatePicker
          isNotUseTimeZone
          onChange={(e) => setUrlParam({ date: moment(e.target.value).format('YYYY-MM-DD') })}
          value={date}
          maxDate={today}
        />
      </div>
      <Grid.Wrapper name="dailyLogs" isKeepOptions className="table-daily-logs">
        <Grid.Row className="table-daily-logs__row-controls">
          <div />
          <Grid.Col name="User" className="table-daily-logs__col--user" />
          <Grid.Col name="Status" className="table-daily-logs__col--status" />
          <Grid.Col name="Entries" className="table-daily-logs__col--entries" />
          <Grid.Col name="Total" className="table-daily-logs__col--total" />
          <Grid.Col name="Notes" className="table-daily-logs__col--notes" />
        </Grid.Row>
        {dailyLogs.map((item) => (
          <Grid.Row
            key={`daily-log-${item.id}`}
            className="table-daily-logs__row daily-log-row"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              history.push(
                `/activity/daily-logs/submit/?userId=${item.user_id}&date=${item.date}`,
              )
            }
          >
            <Grid.ColData className="table-daily-logs__col--user__icon">
              <div className="daily-log-row__icon">
                <BrillMask asLink>
                  <Avatar
                    src={getImageUrl(item.avatar, IMAGES_SIZE.sm)}
                    alt={`${item.first_name} ${item.last_name}`}
                  />
                </BrillMask>
              </div>
            </Grid.ColData>
            <Grid.ColData className="table-daily-logs__col--user__name">
              <div className="daily-log-row__name">{`${item.first_name} ${item.last_name}`}</div>
            </Grid.ColData>
            <Grid.ColData className="table-daily-logs__col--status">
              <div className="daily-log-row__status-info">
                {date === today && (
                  <span
                    className="daily-log-row__status"
                    style={{
                      color: getCurrentStatus(item.entries).color,
                    }}
                  >
                    {getCurrentStatus(item.entries).label}
                  </span>
                )}
                <div className="daily-log-row__entries mobile-only">
                  {item.entries.map((entry) => (
                    <span key={`entry-${entry.id}`} className="daily-log-row__entries__entry">
                      {entry.start_time} - {entry.end_time}{' '}
                      {entry.schedule_type !== 'WORK' &&
                        `(${getScheduleTypeByValue(entry.schedule_type).label})`}
                    </span>
                  ))}
                </div>
              </div>
            </Grid.ColData>
            <Grid.ColData className="table-daily-logs__col--entries desktop-only">
              <div className="daily-log-row__entries">
                {item.entries.map((entry) => (
                  <span key={`entry-${entry.id}`} className="daily-log-row__entries__entry">
                    {entry.start_time} - {entry.end_time}{' '}
                    {
                      `(${entry.schedule_type !== 'WORK'
                        ? getScheduleTypeByValue(entry.schedule_type).label
                        : getWhereByValue(entry.work_place).label})`
                    }
                  </span>
                ))}
              </div>
            </Grid.ColData>
            <Grid.ColData className="table-daily-logs__col--total">
              <div className="daily-log-row__total">
                <div className="daily-log-row__hours">
                  <span>{calculateTotalHours(item.entries)}</span>
                  <span className='daily-log-row__hours__month'>({item.total_hours}h total)</span>
                </div>
              </div>
            </Grid.ColData>
            <Grid.ColData className="table-daily-logs__col--notes">
              <div className="daily-log-row__notes">
                <span>{item.notes || '-'}</span>
                {item.created_at !== item.updated_at && (
                  <span className="daily-log-row__notes__edited-at">
                    (edited at {moment(item.updated_at).format('HH:mm')})
                  </span>
                )}
              </div>
            </Grid.ColData>
          </Grid.Row>
        ))
        }
      </Grid.Wrapper>
      {usersWithNoEntries.length !== 0 && (
        <Grid.Wrapper
          name="noEntries"
          isKeepOptions
          className="table-daily-logs table-daily-logs--no-entries"
        >
          <Grid.Row className="table-daily-logs__row-controls">
            <div />
            <Grid.Col
              name={`No Entries (${usersWithNoEntries.length})`}
              className="table-daily-logs__col--user"
            />
          </Grid.Row>
          {usersWithNoEntries.map((item) => (
            <Grid.Row key={`no-entries-${item.id}`} className="table-daily-logs__row daily-log-row">
              <Grid.ColData className="table-daily-logs__col--user__icon">
                <div className="daily-log-row__icon">
                  <BrillMask asLink>
                    <Avatar
                      src={getImageUrl(item.avatar, IMAGES_SIZE.sm)}
                      alt={`${item.first_name} ${item.last_name}`}
                    />
                  </BrillMask>
                </div>
              </Grid.ColData>
              <Grid.ColData className="table-daily-logs__col--user__name">
                <div className="daily-log-row__name">{`${item.first_name} ${item.last_name}`}</div>
              </Grid.ColData>
            </Grid.Row>
          ))}
        </Grid.Wrapper>
      )}
    </>
  );
};
